import { useEffect, useMemo, useState } from "react";
import { Plus, Trash, X, Wallet, CheckCircle, ArrowRight, ClipboardText, UserSwitch, Bank, Lightning, FilePdf, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useCurrency } from "../../lib/useCurrency";
import { Th, Td } from "./TrainerSchedule";

const SESSION_TYPES = ["60 Minutes", "45 Minutes", "30 Minutes", "Group Session"];

export default function TrainerPayroll() {
  const { symbol } = useCurrency();
  const [view, setView] = useState("staff");
  const [staff, setStaff] = useState([]);
  const [runs, setRuns] = useState([]);
  const [showStaffForm, setShowStaffForm] = useState(false);
  const [editStaff, setEditStaff] = useState(null);
  const [runFor, setRunFor] = useState(null);
  const [activeRun, setActiveRun] = useState(null);
  const [connectCfg, setConnectCfg] = useState({ enabled: false, mode: "stub", country: "US" });
  const [downloadingStmt, setDownloadingStmt] = useState(null);
  const [emailingStmt, setEmailingStmt] = useState(null);

  const downloadStatement = async (s) => {
    if (downloadingStmt) return;
    setDownloadingStmt(s.id);
    try {
      const { data } = await api.get(`/staff/${s.id}/statement`, { responseType: "blob" });
      const blob = new Blob([data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const now = new Date();
      const ym = now.getMonth() === 0 ? `${now.getFullYear() - 1}-12` : `${now.getFullYear()}-${String(now.getMonth()).padStart(2, "0")}`;
      link.download = `Traina_Statement_${(s.name || "staff").replace(/\s+/g, "_")}_${ym}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded statement for ${s.name}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to generate statement");
    } finally {
      setDownloadingStmt(null);
    }
  };

  const emailStatement = async (s) => {
    if (emailingStmt) return;
    if (!s.email) {
      toast.error(`${s.name} has no email on file — add one in Edit first`);
      return;
    }
    setEmailingStmt(s.id);
    try {
      const { data } = await api.post(`/staff/${s.id}/statement/email`);
      if (data.status === "simulated") {
        toast.info(`Statement queued for ${s.email} (Resend not configured — email simulated)`);
      } else {
        toast.success(`Statement emailed to ${s.email}`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to email statement");
    } finally {
      setEmailingStmt(null);
    }
  };

  const load = async () => {
    try {
      const [s, r, c] = await Promise.all([
        api.get("/staff"),
        api.get("/payroll/runs"),
        api.get("/connect/config"),
      ]);
      setStaff(s.data);
      setRuns(r.data);
      setConnectCfg(c.data);
    } catch (_e) {}
  };

  useEffect(() => { load(); }, []);

  const removeStaff = async (id) => {
    if (!window.confirm("Remove this staff member? (Will deactivate if they have linked sessions.)")) return;
    try {
      const { data } = await api.delete(`/staff/${id}`);
      toast.success(data.status === "deactivated" ? "Deactivated (linked sessions)" : "Removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const markRunPaid = async (id) => {
    try {
      await api.patch(`/payroll/runs/${id}/mark-paid`);
      toast.success("Marked paid");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeRun = async (id) => {
    if (!window.confirm("Delete this pending payroll run? Sessions will be unlocked.")) return;
    try {
      await api.delete(`/payroll/runs/${id}`);
      toast.success("Run deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const stats = useMemo(() => {
    const pending = runs.filter((r) => r.status === "pending").reduce((s, r) => s + (r.amount || 0), 0);
    const ytd = runs.filter((r) => r.status === "paid").reduce((s, r) => s + (r.amount || 0), 0);
    return { pending, ytd, activeStaff: staff.filter((s) => s.active).length };
  }, [runs, staff]);

  return (
    <div className="space-y-8" data-testid="trainer-payroll">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="tracker text-brand mb-3">[ Payroll ]</div>
          <h1 className="font-display text-4xl sm:text-5xl">Pay staff.</h1>
          <p className="text-zinc-500 text-xs mt-2">Set rates per staff, assign sessions, then run payroll for any period.</p>
        </div>
      </div>

      <div className="grid grid-cols-3 border border-white/10">
        <Mini label="Active Staff" value={stats.activeStaff} />
        <Mini label="Pending Payroll" value={`${symbol}${stats.pending.toFixed(2)}`} accent money />
        <Mini label="Total Paid" value={`${symbol}${stats.ytd.toFixed(2)}`} money />
      </div>

      <div className={`card-flat p-4 flex items-center justify-between gap-4 flex-wrap ${connectCfg.mode === "live" ? "border-emerald-500/30" : connectCfg.mode === "test" ? "border-brand/40" : "border-amber-500/30"}`} data-testid="connect-banner">
        <div className="flex items-center gap-3">
          <Bank size={22} weight="duotone" className={connectCfg.mode === "live" ? "text-emerald-400" : connectCfg.mode === "test" ? "text-brand-light" : "text-amber-400"} />
          <div>
            <div className="tracker">Stripe Connect Payouts</div>
            <div className="text-sm" data-testid="connect-banner-status">
              {connectCfg.mode === "live" && (
                <span className="text-emerald-400">Live mode — real bank transfers enabled ({connectCfg.country})</span>
              )}
              {connectCfg.mode === "test" && (
                <span className="text-brand-light">Test mode — onboarding flow real, no real funds move ({connectCfg.country})</span>
              )}
              {connectCfg.mode === "stub" && (
                <span className="text-amber-400">Stub mode — set STRIPE_CONNECT_SECRET_KEY in backend/.env to enable real Stripe Connect</span>
              )}
            </div>
            {connectCfg.mode !== "stub" && !connectCfg.webhook_configured && (
              <div className="tracker text-[10px] text-amber-400 mt-1" data-testid="connect-webhook-warning">
                ! No webhook secret set — payout/account status will not auto-sync. Add STRIPE_CONNECT_WEBHOOK_SECRET.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {[
          { key: "staff", label: "Staff", count: staff.length },
          { key: "runs", label: "Pay Runs", count: runs.length },
        ].map((v) => (
          <button
            key={v.key}
            onClick={() => setView(v.key)}
            className={`tracker px-5 py-2 border ${view === v.key ? "bg-brand border-brand text-white" : "border-white/10 hover:border-white/40 text-zinc-400"}`}
            data-testid={`payroll-view-${v.key}`}
          >
            {v.label} ({v.count})
          </button>
        ))}
      </div>

      {view === "staff" && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={() => { setEditStaff(null); setShowStaffForm(true); }} className="btn-primary" data-testid="add-staff-btn">
              <Plus size={16} weight="bold" /> Add staff
            </button>
          </div>
          <div className="card-flat overflow-x-auto">
            {staff.length === 0 ? (
              <div className="p-16 text-center text-zinc-500 tracker">No staff yet — add your first one</div>
            ) : (
              <table className="w-full" data-testid="staff-table">
                <thead>
                  <tr className="border-b border-white/10">
                    <Th>Name</Th><Th>Email · Phone</Th><Th>Rates ({symbol} per session)</Th><Th>Stripe</Th><Th>Auto-pay</Th><Th>Login</Th><Th>Status</Th><Th align="right">Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {staff.map((s) => (
                    <tr key={s.id} className="border-b border-white/5 hover:bg-[#1A1A1A]" data-testid={`staff-row-${s.id}`}>
                      <Td>
                        <div className="flex items-center gap-2">
                          <span
                            aria-hidden
                            className="inline-block h-3 w-3 rounded-sm border border-white/20 shrink-0"
                            style={{ background: s.color || "transparent" }}
                            data-testid={`staff-color-swatch-${s.id}`}
                            title={s.color ? `Calendar tint ${s.color}` : "No calendar colour set"}
                          />
                          <div className="font-semibold">{s.name}</div>
                        </div>
                        {s.payroll_percent != null && (
                          <div className="tracker text-[10px] text-brand-light mt-0.5" data-testid={`staff-payroll-pct-${s.id}`}>
                            {s.payroll_percent}% of session
                          </div>
                        )}
                      </Td>
                      <Td>
                        <div className="font-mono text-xs">{s.email || "—"}</div>
                        <div className="font-mono text-[10px] text-zinc-500">{s.phone || "—"}</div>
                      </Td>
                      <Td>
                        <div className="flex gap-2 flex-wrap">
                          {SESSION_TYPES.map((t) => (
                            <span key={t} className="tracker text-[10px] px-2 py-1 border border-white/10" title={t}>
                              <span className="money">{t.split(" ")[0]}: ${Number((s.rates || {})[t] || 0).toFixed(0)}</span>
                            </span>
                          ))}
                        </div>
                      </Td>
                      <Td><ConnectPill staff={s} cfg={connectCfg} onChanged={load} /></Td>
                      <Td><AutoPayPill staff={s} onChanged={load} /></Td>
                      <Td><LoginPill staff={s} onChanged={load} /></Td>
                      <Td>
                        {s.active ? (
                          <span className="tracker px-2 py-1 border border-emerald-500/30 text-emerald-400">active</span>
                        ) : (
                          <span className="tracker px-2 py-1 border border-zinc-500/30 text-zinc-500">inactive</span>
                        )}
                      </Td>
                      <Td align="right">
                        <div className="flex items-center gap-2 justify-end">
                          <button onClick={() => setRunFor(s)} disabled={!s.active} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`run-payroll-${s.id}`} title="New pay run">
                            <Wallet size={14} weight="bold" />
                          </button>
                          <button onClick={() => downloadStatement(s)} disabled={downloadingStmt === s.id} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`statement-${s.id}`} title={`Download last month's statement PDF for ${s.name}`}>
                            <FilePdf size={14} weight="bold" />
                          </button>
                          <button onClick={() => emailStatement(s)} disabled={emailingStmt === s.id} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`email-statement-${s.id}`} title={s.email ? `Email last month's statement PDF to ${s.email}` : "No email on file — add one to use this"}>
                            <EnvelopeSimple size={14} weight="bold" />
                          </button>
                          <button onClick={() => { setEditStaff(s); setShowStaffForm(true); }} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`edit-staff-${s.id}`} title="Edit">
                            <UserSwitch size={14} weight="bold" />
                          </button>
                          <button onClick={() => removeStaff(s.id)} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`delete-staff-${s.id}`}>
                            <Trash size={14} weight="bold" />
                          </button>
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {view === "runs" && (
        <div className="card-flat overflow-x-auto">
          {runs.length === 0 ? (
            <div className="p-16 text-center text-zinc-500 tracker">No payroll runs yet — go to Staff and click the wallet icon to create one</div>
          ) : (
            <table className="w-full" data-testid="runs-table">
              <thead>
                <tr className="border-b border-white/10">
                  <Th>Created</Th><Th>Staff</Th><Th>Period</Th><Th>Sessions</Th><Th>Amount</Th><Th>Status · Payout</Th><Th align="right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className="border-b border-white/5 hover:bg-[#1A1A1A]" data-testid={`run-row-${r.id}`}>
                    <Td><span className="tracker">{new Date(r.created_at).toLocaleDateString()}</span></Td>
                    <Td><div className="font-semibold">{r.staff?.name || "—"}</div></Td>
                    <Td><span className="tracker">{r.period_start} → {r.period_end}</span></Td>
                    <Td>{(r.session_ids || []).length}</Td>
                    <Td><span className="font-mono font-semibold money">${(r.amount || 0).toFixed(2)}</span></Td>
                    <Td>
                      <div className="flex items-center gap-2 flex-wrap">
                        {r.status === "paid" ? (
                          <span className="tracker px-2 py-1 border border-emerald-500/30 text-emerald-400">paid</span>
                        ) : (
                          <span className="tracker px-2 py-1 border border-amber-500/30 text-amber-400">pending</span>
                        )}
                        {r.payout_method && (
                          <span className="tracker text-[10px] px-1.5 py-1 border border-white/10 text-zinc-400" title={r.stripe_transfer_id || ""}>
                            {r.payout_method === "stripe_connect" ? "via Stripe" : r.payout_method === "stripe_stub" ? "stub transfer" : "manual"}
                          </span>
                        )}
                        {r.auto_pay && (
                          <span className="tracker text-[10px] px-1.5 py-1 border border-brand-light/30 text-brand-light" title="Created by the weekly auto-pay scheduler" data-testid={`auto-pay-badge-${r.id}`}>
                            auto
                          </span>
                        )}
                      </div>
                    </Td>
                    <Td align="right">
                      <div className="flex items-center gap-2 justify-end">
                        <button onClick={() => setActiveRun(r.id)} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`view-run-${r.id}`} title="View detail">
                          <ClipboardText size={14} weight="bold" />
                        </button>
                        {r.status !== "paid" && (
                          <>
                            <button onClick={() => markRunPaid(r.id)} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`pay-run-${r.id}`} title="Mark paid">
                              <CheckCircle size={14} weight="bold" />
                            </button>
                            <button onClick={() => removeRun(r.id)} className="btn-ghost !py-1.5 !px-2 !text-[10px]" data-testid={`delete-run-${r.id}`}>
                              <Trash size={14} weight="bold" />
                            </button>
                          </>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {showStaffForm && (
        <StaffForm
          existing={editStaff}
          onClose={() => { setShowStaffForm(false); setEditStaff(null); }}
          onDone={load}
        />
      )}
      {runFor && <NewPayrollRunModal staff={runFor} onClose={() => setRunFor(null)} onDone={load} />}
      {activeRun && <PayrollRunDetail runId={activeRun} onClose={() => setActiveRun(null)} />}
    </div>
  );
}

function Mini({ label, value, accent, money }) {
  return (
    <div className={`p-6 border-r border-b border-white/10 ${accent ? "bg-[#0E1424]" : ""}`}>
      <div className="tracker mb-3">{label}</div>
      <div className={`font-display text-3xl ${money ? "money" : ""} ${accent ? "text-brand-light" : ""}`}>{value}</div>
    </div>
  );
}

function StaffForm({ existing, onClose, onDone }) {
  const initial = existing || {
    name: "",
    email: "",
    phone: "",
    notes: "",
    rates: { "60 Minutes": 40, "45 Minutes": 30, "30 Minutes": 20, "Group Session": 15 },
    active: true,
    color: "",
    payroll_percent: "",
  };
  const [form, setForm] = useState({ ...initial, rates: { ...(initial.rates || {}) } });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const pct = form.payroll_percent === "" || form.payroll_percent == null ? null : Number(form.payroll_percent);
      const payload = {
        name: form.name,
        email: form.email || null,
        phone: form.phone || null,
        notes: form.notes,
        rates: Object.fromEntries(Object.entries(form.rates).map(([k, v]) => [k, Number(v) || 0])),
        active: form.active,
        color: (form.color || "").trim() || null,
        payroll_percent: Number.isFinite(pct) ? pct : null,
      };
      if (existing) {
        await api.patch(`/staff/${existing.id}`, payload);
        toast.success("Staff updated");
      } else {
        await api.post("/staff", payload);
        toast.success("Staff added");
      }
      onDone(); onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="card-flat w-full max-w-md p-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="staff-form-modal">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="tracker text-brand mb-2">[ {existing ? "Edit staff" : "New staff"} ]</div>
            <h2 className="font-display text-3xl">{existing ? form.name : "Add."}</h2>
          </div>
          <button onClick={onClose}><X size={20} weight="bold" /></button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="tracker block mb-2">Name</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 border" data-testid="staff-name-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="tracker block mb-2">Email</label>
              <input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 border" data-testid="staff-email-input" />
            </div>
            <div>
              <label className="tracker block mb-2">Phone</label>
              <input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-4 py-3 border" data-testid="staff-phone-input" />
            </div>
          </div>

          <div>
            <label className="tracker block mb-2">Pay rate per session</label>
            <div className="grid grid-cols-2 gap-2">
              {SESSION_TYPES.map((t) => (
                <div key={t} className="flex items-center gap-2">
                  <span className="tracker text-[10px] w-20">{t}</span>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={form.rates[t] ?? 0}
                    onChange={(e) => setForm({ ...form, rates: { ...form.rates, [t]: e.target.value } })}
                    className="flex-1 px-3 py-2 border"
                    data-testid={`staff-rate-${t.replace(/\s+/g, "-").toLowerCase()}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="tracker block mb-2">Calendar colour</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.color || "var(--brand)"}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="h-10 w-14 border bg-transparent p-1 cursor-pointer"
                  data-testid="staff-color-input"
                />
                <input
                  type="text"
                  value={form.color || ""}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  placeholder="var(--brand)"
                  className="flex-1 px-3 py-2 border bg-transparent font-mono text-xs"
                  data-testid="staff-color-hex"
                />
              </div>
              <p className="text-[10px] text-zinc-500 mt-1">Sessions run by this staff show this tint on the calendar.</p>
            </div>
            <div>
              <label className="tracker block mb-2">Payroll % of session</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={form.payroll_percent ?? ""}
                  onChange={(e) => setForm({ ...form, payroll_percent: e.target.value })}
                  placeholder="e.g. 60"
                  className="w-24 px-3 py-2 border bg-transparent"
                  data-testid="staff-payroll-pct-input"
                />
                <span className="tracker text-zinc-500">%</span>
              </div>
              <p className="text-[10px] text-zinc-500 mt-1">When set, staff pay defaults to price × this %. Leave blank to use rate table below.</p>
            </div>
          </div>

          <label className="flex items-center justify-between cursor-pointer card-flat p-3">
            <span className="tracker">Active</span>
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="staff-active-toggle" />
          </label>

          <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="staff-submit">
            {loading ? "Saving…" : existing ? "Save changes" : "Add staff"}
          </button>
        </form>
      </div>
    </div>
  );
}

function NewPayrollRunModal({ staff, onClose, onDone }) {
  const { symbol } = useCurrency();
  const today = new Date();
  const first = new Date(today.getUTCFullYear(), today.getUTCMonth(), 1);
  const last = new Date(today.getUTCFullYear(), today.getUTCMonth() + 1, 0);
  const toISO = (d) => d.toISOString().slice(0, 10);

  const [from, setFrom] = useState(toISO(first));
  const [to, setTo] = useState(toISO(last));
  const [preview, setPreview] = useState({ sessions: [], total: 0, count: 0 });
  const [selected, setSelected] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState("completed");
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState(`${staff.name} · ${from} → ${to}`);

  const loadPreview = async () => {
    try {
      const { data } = await api.get("/payroll/preview", {
        params: { staff_id: staff.id, from_date: `${from}T00:00:00`, to_date: `${to}T23:59:59`, status_filter: statusFilter },
      });
      setPreview(data);
      setSelected(new Set(data.sessions.map((s) => s.id)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => { loadPreview(); /* eslint-disable-next-line */ }, [from, to, statusFilter]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const selectedTotal = useMemo(
    () => preview.sessions.filter((s) => selected.has(s.id)).reduce((sum, s) => sum + (Number(s.staff_pay) || 0), 0),
    [preview, selected]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (selected.size === 0) return toast.error("Select at least one session");
    setLoading(true);
    try {
      await api.post("/payroll/runs", {
        staff_id: staff.id,
        session_ids: Array.from(selected),
        period_start: from,
        period_end: to,
        notes,
      });
      toast.success(`Payroll run created · ${symbol}${selectedTotal.toFixed(2)}`);
      onDone(); onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="card-flat w-full max-w-2xl p-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="new-payroll-modal">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="tracker text-brand mb-2">[ New pay run ]</div>
            <h2 className="font-display text-3xl">{staff.name}</h2>
          </div>
          <button onClick={onClose}><X size={20} weight="bold" /></button>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <div>
            <label className="tracker block mb-2">From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full px-3 py-2 border" data-testid="run-from-date" />
          </div>
          <div>
            <label className="tracker block mb-2">To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-full px-3 py-2 border" data-testid="run-to-date" />
          </div>
          <div>
            <label className="tracker block mb-2">Status filter</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full px-3 py-2 border" data-testid="run-status-filter">
              <option value="completed">Completed only</option>
              <option value="scheduled">Scheduled only</option>
              <option value="any">Any status</option>
            </select>
          </div>
        </div>

        <div className="card-flat mb-4">
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <div className="tracker">{preview.count} eligible · {selected.size} selected</div>
            <div className="font-display text-2xl money">${selectedTotal.toFixed(2)}</div>
          </div>
          {preview.sessions.length === 0 ? (
            <div className="p-8 text-center text-zinc-500 tracker">No eligible sessions in this range</div>
          ) : (
            <div className="max-h-72 overflow-y-auto">
              {preview.sessions.map((s) => (
                <label key={s.id} className="px-4 py-3 border-b border-white/5 last:border-b-0 flex items-center gap-3 hover:bg-[#1A1A1A] cursor-pointer" data-testid={`preview-${s.id}`}>
                  <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggle(s.id)} />
                  <div className="flex-1">
                    <div className="font-mono text-sm">{new Date(s.start_time).toLocaleDateString()} {new Date(s.start_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                    <div className="text-xs text-zinc-500">{s.session_type} · {s.client?.name} · {s.status}</div>
                  </div>
                  <div className="font-mono font-semibold text-brand-light money">${Number(s.staff_pay || 0).toFixed(2)}</div>
                </label>
              ))}
            </div>
          )}
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="tracker block mb-2">Notes</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full px-3 py-2 border" />
          </div>
          <button type="submit" disabled={loading || selected.size === 0} className="btn-primary w-full justify-center" data-testid="create-run-submit">
            {loading ? "Creating…" : <>Create pay run · <span className="money">${selectedTotal.toFixed(2)}</span></>} <ArrowRight size={14} weight="bold" />
          </button>
        </form>
      </div>
    </div>
  );
}

function PayrollRunDetail({ runId, onClose }) {
  const [run, setRun] = useState(null);
  useEffect(() => {
    (async () => {
      try { setRun((await api.get(`/payroll/runs/${runId}`)).data); } catch (_e) {}
    })();
  }, [runId]);
  if (!run) return null;
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="card-flat w-full max-w-2xl p-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="run-detail-modal">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="tracker text-brand mb-2">[ Pay run detail ]</div>
            <h2 className="font-display text-3xl">{run.staff?.name}</h2>
            <div className="tracker mt-1">{run.period_start} → {run.period_end} · {run.status}</div>
          </div>
          <button onClick={onClose}><X size={20} weight="bold" /></button>
        </div>
        <div className="card-flat p-4 mb-4 flex items-center justify-between">
          <div className="tracker">Total</div>
          <div className="font-display text-3xl money">${(run.amount || 0).toFixed(2)}</div>
        </div>
        <div className="card-flat">
          {(run.sessions || []).map((s) => (
            <div key={s.id} className="px-4 py-3 border-b border-white/5 last:border-b-0 flex items-center gap-3">
              <div className="flex-1">
                <div className="font-mono text-sm">{new Date(s.start_time).toLocaleDateString()} · {s.session_type}</div>
                <div className="text-xs text-zinc-500">{s.client?.name} · {s.status}</div>
              </div>
              <div className="font-mono font-semibold text-brand-light money">${Number(s.staff_pay || 0).toFixed(2)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ConnectPill({ staff, cfg, onChanged }) {
  const [busy, setBusy] = useState(false);

  const startOnboarding = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/staff/${staff.id}/connect/onboarding-link`, {
        return_url: `${window.location.origin}/trainer/payroll`,
        refresh_url: `${window.location.origin}/trainer/payroll`,
      });
      if (data.stub) {
        toast.success("Stub onboarding link generated — click Simulate to flip to active");
        onChanged();
      } else {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const refreshStatus = async () => {
    setBusy(true);
    try { await api.get(`/staff/${staff.id}/connect/status`); onChanged(); }
    catch (_e) {} finally { setBusy(false); }
  };

  const simulateActive = async () => {
    setBusy(true);
    try {
      await api.post(`/staff/${staff.id}/connect/simulate-active`);
      toast.success("Stub set to active — payouts will route through Stripe (stub mode)");
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const status = staff.stripe_status || "not_connected";
  const cls = {
    not_connected: "border-zinc-500/30 text-zinc-500",
    pending: "border-amber-500/30 text-amber-400",
    stub: "border-amber-500/30 text-amber-400",
    active: "border-emerald-500/30 text-emerald-400",
  }[status] || "border-zinc-500/30 text-zinc-500";
  const label = status === "not_connected" ? "Not connected" : status === "stub" ? "Stub" : status;

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`connect-pill-${staff.id}`}>
      <span className={`tracker px-2 py-1 border ${cls}`}>{label}</span>
      {status === "not_connected" && (
        <button onClick={startOnboarding} disabled={busy} className="btn-ghost !py-1 !px-2 !text-[10px]" title="Start Stripe onboarding" data-testid={`onboard-${staff.id}`}>
          <Bank size={12} weight="bold" /> Onboard
        </button>
      )}
      {status === "pending" && cfg.enabled && (
        <button onClick={refreshStatus} disabled={busy} className="btn-ghost !py-1 !px-2 !text-[10px]" data-testid={`refresh-${staff.id}`}>
          Refresh
        </button>
      )}
      {!cfg.enabled && (status === "stub" || status === "pending") && (
        <button onClick={simulateActive} disabled={busy} className="btn-ghost !py-1 !px-2 !text-[10px]" title="Stub-only: pretend onboarding finished" data-testid={`simulate-${staff.id}`}>
          <Lightning size={12} weight="bold" /> Simulate
        </button>
      )}
    </div>
  );
}


const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function LoginPill({ staff, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null);

  const issue = async (reset) => {
    if (!staff.email) { toast.error(`${staff.name} needs an email first — add one in Edit`); return; }
    if (reset && !window.confirm(`Reset ${staff.name}'s password? Their current sessions will be signed out.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/staff/${staff.id}/login`, {});
      setIssued(data);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!window.confirm(`Remove ${staff.name}'s login? They'll no longer be able to sign in.`)) return;
    setBusy(true);
    try {
      await api.delete(`/staff/${staff.id}/login`);
      toast.success("Login removed");
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(`Email: ${issued.email}\nPassword: ${issued.temp_password}`); toast.success("Copied"); } catch (_e) { toast.error("Copy failed"); }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`login-pill-${staff.id}`}>
      {staff.has_login ? (
        <>
          <span className="tracker text-[9px] px-2 py-1 border border-emerald-500/30 text-emerald-400" title={`Signs in as ${staff.login_email}`} data-testid={`login-active-${staff.id}`}>INSTRUCTOR</span>
          <button onClick={() => issue(true)} disabled={busy} className="tracker text-[9px] px-2 py-1 border border-white/10 text-zinc-400 hover:opacity-80" data-testid={`login-reset-${staff.id}`}>reset pw</button>
          <button onClick={revoke} disabled={busy} className="tracker text-[9px] px-2 py-1 border border-red-500/30 text-red-400 hover:opacity-80" data-testid={`login-revoke-${staff.id}`}>remove</button>
        </>
      ) : (
        <button onClick={() => issue(false)} disabled={busy} className="tracker text-[9px] px-2 py-1 border border-brand-light/40 text-brand-light hover:opacity-80" title={staff.email ? "Give this instructor their own sign-in for group sessions" : "Add an email first"} data-testid={`login-create-${staff.id}`}>
          + LOGIN
        </button>
      )}
      {issued && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setIssued(null)}>
          <div className="card-flat w-full max-w-md p-8" onClick={(e) => e.stopPropagation()} data-testid="login-issued-modal">
            <div className="tracker text-brand mb-2">[ Instructor login {issued.status === "reset" ? "reset" : "created"} ]</div>
            <h2 className="font-display text-3xl mb-4">{staff.name}</h2>
            <p className="text-zinc-400 text-sm mb-4">{issued.emailed ? "We've emailed these details to them. " : ""}Share them now — the password is only shown once.</p>
            <div className="border border-white/10 p-4 font-mono text-sm space-y-1">
              <div><span className="text-zinc-500">email </span><span data-testid="login-issued-email">{issued.email}</span></div>
              <div><span className="text-zinc-500">password </span><span data-testid="login-issued-password">{issued.temp_password}</span></div>
            </div>
            <p className="tracker text-zinc-500 text-[9px] mt-3">They sign in at /login and land in the Instructor portal — group sessions only, no client data.</p>
            <div className="flex gap-2 mt-5">
              <button onClick={copy} className="btn-ghost flex-1 justify-center !text-xs" data-testid="login-issued-copy"><ClipboardText size={14} /> Copy</button>
              <button onClick={() => setIssued(null)} className="btn-primary flex-1 justify-center !text-xs" data-testid="login-issued-done">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AutoPayPill({ staff, onChanged }) {
  const [busy, setBusy] = useState(false);
  const enabled = !!staff.auto_pay_enabled;
  const day = Number.isInteger(staff.auto_pay_day) ? staff.auto_pay_day : 6;
  const stmtOn = !!staff.auto_email_statement;

  const patch = async (payload) => {
    setBusy(true);
    try {
      await api.patch(`/staff/${staff.id}`, payload);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggle = () => patch({ auto_pay_enabled: !enabled, auto_pay_day: day });
  const setDay = (e) => patch({ auto_pay_day: Number(e.target.value) });
  const toggleStmt = () => patch({ auto_email_statement: !stmtOn });

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`autopay-pill-${staff.id}`}>
      <button
        onClick={toggle}
        disabled={busy}
        className={`tracker px-2 py-1 border ${enabled ? "border-emerald-500/30 text-emerald-400" : "border-zinc-500/30 text-zinc-500"} hover:opacity-80`}
        data-testid={`autopay-toggle-${staff.id}`}
        title="Auto-bundle last 7 days of completed sessions and pay out on the chosen weekday"
      >
        {enabled ? "ON" : "OFF"}
      </button>
      {enabled && (
        <select
          value={day}
          onChange={setDay}
          disabled={busy}
          className="bg-transparent border border-white/10 text-[10px] tracker px-2 py-1"
          data-testid={`autopay-day-${staff.id}`}
        >
          {DAY_LABELS.map((d, i) => (
            <option key={i} value={i} className="bg-zinc-900">{d}</option>
          ))}
        </select>
      )}
      <button
        onClick={toggleStmt}
        disabled={busy || !staff.email}
        className={`tracker text-[9px] px-2 py-1 border ${stmtOn ? "border-brand-light/40 text-brand-light" : "border-zinc-600/30 text-zinc-500"} hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed`}
        data-testid={`stmt-toggle-${staff.id}`}
        title={!staff.email ? "Add an email first" : (stmtOn ? "Statement PDF will be attached to every payout email — click to disable" : "Click to attach a monthly statement PDF to every payout email")}
      >
        + STMT
      </button>
      {enabled && staff.auto_pay_last_run_at && (
        <span className="tracker text-[9px] text-zinc-500" title={staff.auto_pay_last_run_at}>
          last {new Date(staff.auto_pay_last_run_at).toLocaleDateString()}
        </span>
      )}
      {enabled && staff.auto_pay_last_error && (
        <span className="tracker text-[9px] text-[#FF6B61]" title={staff.auto_pay_last_error}>! err</span>
      )}
    </div>
  );
}

